'use client';

import { useMemo, useState } from 'react';
import { computeRentVsBuy, type RentVsBuyInputs } from '@/lib/browser-rent-vs-buy';

const DEFAULTS: RentVsBuyInputs = {
  homePrice: '400000',
  downPayment: '80000',
  mortgageRatePct: '6.5',
  termYears: '30',
  propertyTaxPct: '1.1',
  maintenancePct: '1',
  homeAppreciationPct: '3',
  sellingCostPct: '6',
  monthlyRent: '2000',
  rentGrowthPct: '3',
  investmentReturnPct: '6',
  yearsToStay: '10',
};

const FIELDS: { key: keyof RentVsBuyInputs; label: string; placeholder: string }[] = [
  { key: 'homePrice', label: 'Home price', placeholder: '400000' },
  { key: 'downPayment', label: 'Down payment', placeholder: '80000' },
  { key: 'mortgageRatePct', label: 'Mortgage rate (% / year)', placeholder: '6.5' },
  { key: 'termYears', label: 'Mortgage term (years)', placeholder: '30' },
  { key: 'propertyTaxPct', label: 'Property tax (% of value / year)', placeholder: '1.1' },
  { key: 'maintenancePct', label: 'Maintenance (% of value / year)', placeholder: '1' },
  { key: 'homeAppreciationPct', label: 'Home appreciation (% / year)', placeholder: '3' },
  { key: 'sellingCostPct', label: 'Selling cost at sale (%)', placeholder: '6' },
  { key: 'monthlyRent', label: 'Monthly rent', placeholder: '2000' },
  { key: 'rentGrowthPct', label: 'Rent growth (% / year)', placeholder: '3' },
  { key: 'investmentReturnPct', label: 'Investment return (% / year)', placeholder: '6' },
  { key: 'yearsToStay', label: 'Years to stay', placeholder: '10' },
];

function formatMoney(value: number): string {
  const rounded = Math.round(value);
  return rounded.toLocaleString('en-US');
}

export default function BrowserRentVsBuyCalculator() {
  const [inputs, setInputs] = useState<RentVsBuyInputs>(DEFAULTS);

  const result = useMemo(() => computeRentVsBuy(inputs), [inputs]);

  function update(key: keyof RentVsBuyInputs, value: string) {
    setInputs((prev) => ({ ...prev, [key]: value }));
  }

  const verdict = (() => {
    if (!result.ok) return null;
    if (result.cheaper === 'buy') {
      return `Buying is cheaper by about ${formatMoney(result.difference ?? 0)} over this period.`;
    }
    if (result.cheaper === 'rent') {
      return `Renting is cheaper by about ${formatMoney(result.difference ?? 0)} over this period.`;
    }
    return 'Renting and buying come out about even over this period.';
  })();

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Housing decision</span>
        <h2 className="tool-live-title">Rent vs buy calculator</h2>
        <p className="tool-live-subtitle">
          Compare the true cost of renting against buying a home over the years you plan to stay,
          including mortgage, taxes, maintenance, appreciation, and the return you could earn by
          investing your down payment instead.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {FIELDS.map((field) => (
              <label className="tool-live-field" key={field.key}>
                <span>{field.label}</span>
                <input
                  inputMode="decimal"
                  onChange={(event) => update(field.key, event.target.value)}
                  placeholder={field.placeholder}
                  type="text"
                  value={inputs[field.key]}
                />
              </label>
            ))}
          </div>
          <p className="tool-live-panel-note">
            Costs are shown without a currency symbol so they work anywhere. This is an estimate,
            not financial advice. All numbers stay on your device.
          </p>
        </section>

        <section className="tool-live-preview">
          {!result.ok ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {result.message}
            </p>
          ) : (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-field">
                  <span>Monthly mortgage (P&amp;I)</span>
                  <input readOnly type="text" value={formatMoney(result.monthlyMortgage ?? 0)} />
                </div>
                <div className="tool-live-field">
                  <span>Net cost of buying</span>
                  <input readOnly type="text" value={formatMoney(result.totalBuy ?? 0)} />
                </div>
                <div className="tool-live-field">
                  <span>Net cost of renting</span>
                  <input readOnly type="text" value={formatMoney(result.totalRent ?? 0)} />
                </div>
                <div className="tool-live-field">
                  <span>Break-even year</span>
                  <input
                    readOnly
                    type="text"
                    value={
                      result.breakEvenYear
                        ? `Year ${result.breakEvenYear}`
                        : 'Not within the period'
                    }
                  />
                </div>
              </div>

              {verdict ? (
                <p className="status-message status-success" style={{ marginTop: '0.75rem' }}>
                  {verdict}
                </p>
              ) : null}

              <div
                style={{
                  marginTop: '1rem',
                  maxHeight: '320px',
                  overflow: 'auto',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '10px',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.82rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <thead>
                    <tr>
                      {['Year', 'Buy (net)', 'Rent (net)', 'Home value', 'Equity'].map((label) => (
                        <th
                          key={label}
                          style={{
                            position: 'sticky',
                            top: 0,
                            background: 'rgba(100, 116, 139, 0.18)',
                            backdropFilter: 'blur(4px)',
                            textAlign: label === 'Year' ? 'left' : 'right',
                            padding: '0.45rem 0.6rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(result.years ?? []).map((row) => (
                      <tr key={row.year}>
                        <td
                          style={{
                            padding: '0.35rem 0.6rem',
                            borderTop: '1px solid rgba(148, 163, 184, 0.2)',
                            textAlign: 'left',
                            opacity: 0.7,
                          }}
                        >
                          {row.year}
                        </td>
                        <td style={cellStyle}>{formatMoney(row.buyCumulative)}</td>
                        <td style={cellStyle}>{formatMoney(row.rentCumulative)}</td>
                        <td style={cellStyle}>{formatMoney(row.homeValue)}</td>
                        <td style={cellStyle}>{formatMoney(row.equity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="tool-live-panel-note">
                Buy (net) counts every dollar spent minus the equity you would keep if you sold that
                year. Rent (net) counts rent paid minus the growth on your invested down payment.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  borderTop: '1px solid rgba(148, 163, 184, 0.2)',
  textAlign: 'right',
  whiteSpace: 'nowrap',
};
